import { Link } from "@circleco/compass/components/Link";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

import { UpgradeFigureSkeleton } from "./UpgradeLoadingSkeletons";

const i18nRoot = "paywall_checkout.offer_page.upgrade";

const trialLabel = (preview: UpgradePreview) =>
  preview.trial.disposition === "target" ? null : preview.trial.label;

interface UpgradeFinancialSummaryProps {
  isOrderDetailsOpen: boolean;
  /**
   * A different price was just selected. The previous preview is still in hand, so
   * the labels and layout stay while every figure that depends on the selection
   * gives way to a placeholder rather than showing numbers for the wrong price.
   */
  isRefreshing: boolean;
  isSticky?: boolean;
  onOpenOrderDetails: (button: HTMLButtonElement | HTMLAnchorElement) => void;
  preview: UpgradePreview;
}

export const UpgradeFinancialSummary = ({
  isOrderDetailsOpen,
  isRefreshing,
  isSticky = false,
  onOpenOrderDetails,
  preview,
}: UpgradeFinancialSummaryProps) => (
  <div
    className={classNames("w-full max-w-xl", {
      "space-y-2 [@container(min-width:768px)]:space-y-3": isSticky,
      "space-y-3": !isSticky,
    })}
    aria-live="polite"
    aria-busy={isRefreshing}
  >
    {preview.today.previous_plan_credit && (
      <div
        className="flex justify-between gap-4"
        data-testid="upgrade-plan-credit"
      >
        <Typography variant="body-sm">
          {t([i18nRoot, "previous_plan_credit"])}
        </Typography>
        {isRefreshing ? (
          <UpgradeFigureSkeleton />
        ) : (
          <Typography variant="body-sm" color="secondary">
            {preview.today.previous_plan_credit.formatted}
          </Typography>
        )}
      </div>
    )}
    {!isRefreshing && trialLabel(preview) && (
      <Typography variant="body-sm" color="secondary">
        {trialLabel(preview)}
      </Typography>
    )}
    <div
      className="flex justify-between gap-4"
      data-testid="upgrade-financial-summary"
    >
      <Typography variant="body-md">
        <strong>
          {t([
            i18nRoot,
            preview.execution_lane === "in_place" &&
            preview.trial.disposition === "target"
              ? "estimated_total_due_today"
              : "total_due_today",
          ])}
        </strong>
      </Typography>
      {isRefreshing ? (
        <UpgradeFigureSkeleton className="h-5 w-24" />
      ) : (
        <Typography variant="body-md">
          <strong>{preview.today.total.formatted}</strong>
        </Typography>
      )}
    </div>
    <div
      className={classNames("flex items-center justify-between gap-4", {
        "pb-4": !isSticky,
        "[@container(min-width:768px)]:pb-4": isSticky,
      })}
      data-testid="upgrade-details-savings-row"
    >
      {/* A link rather than a ghost button, so the label carries no inset and
          shares a left edge with the summary rows above it. */}
      <Link
        asButton
        variant="custom"
        typography="body-sm"
        color="primary"
        style={{ textDecoration: "underline" }}
        type="button"
        data-testid="upgrade-view-order-details"
        aria-haspopup="dialog"
        aria-expanded={isOrderDetailsOpen}
        disabled={isRefreshing}
        onClick={event => onOpenOrderDetails(event.currentTarget)}
      >
        {t([i18nRoot, "view_order_details"])}
      </Link>
      {preview.labels.savings &&
        (isRefreshing ? (
          <UpgradeFigureSkeleton className="w-24" />
        ) : (
          <Typography
            variant="body-sm"
            color="success"
            data-testid="upgrade-savings"
          >
            {preview.labels.savings.label}
          </Typography>
        ))}
    </div>
  </div>
);
